import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Card, TextField } from '@totalsoft/rocket-ui';
import { Grid, FormControlLabel, Switch, Link, Paper } from '@mui/material';
import { get } from '@totalsoft/react-state-lens';
import { emptyString } from 'constants/emptyConst';
import { durationCondition } from 'constants/rtiBehaviour';
import { env } from 'utils/env';
import { ParamsContext } from 'providers/ParamsContext';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';

const periodicities = [
  { id: 1, code: 'Monthly', value: 1 },
  { id: 2, code: 'Quarterly', value: 3 },
  { id: 3, code: 'Yearly', value: 12 }
];
const RtiInsuranceGeneralData = ({ lens, onUpdatePaymentPeriodicity, disabled }) => {
  const { t } = useTranslation();

  const { cvsChAmount } = useContext(ParamsContext);
  const { menuInsurance: hasRightToOpenInsurance } = useContext(GlobalRightsContext);

  // #region Memos

  const duration = useMemo(
    () => ((lens.duration |> get) <= durationCondition.contractMaxDuration ? lens.duration |> get : durationCondition.contractMaxDuration),
    [lens]
  );
  const insuranceAmount = useMemo(() => lens.insuranceAmount |> get, [lens]);
  const readOnly = useMemo(() => (lens.financedInsurance |> get) === true || disabled, [disabled, lens]);
  const insuranceOfferURL = useMemo(() => {
    if ((lens?.externalInsuranceDocumentId |> get) !== null && (lens?.externalInsuranceDocumentNumber |> get) !== null) {
      return `${env.REACT_APP_LSNG_INSURANCE_URL}/insurance-documents/${lens.externalInsuranceDocumentId |> get}`;
    }
  }, [lens]);

  // #endregion

  return (
    <Card title={t('Offer.Insurance.GeneralData')}>
      <Grid container spacing={2} alignItems={'flex-end'}>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_currency'}
            fullWidth
            label={t('Offer.Insurance.Currency')}
            disabled
            value={(lens.currencyCode |> get) || emptyString}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_value'}
            fullWidth
            disabled
            isNumeric
            label={t('Offer.Insurance.InsuranceValuePerGood')}
            value={(lens.insuranceAmountPerGood |> get) || emptyString}
            inputProps={{ decimalScale: cvsChAmount, thousandSeparator: true }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_duration'}
            fullWidth
            disabled
            label={t('Offer.Insurance.InsuranceDuration')}
            value={duration || emptyString}
            isNumeric
            inputProps={{
              decimalScale: 0,
              thousandSeparator: false
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_insuranceAmount'}
            fullWidth
            disabled
            isNumeric
            label={t('Offer.Insurance.TotalValue')}
            value={insuranceAmount || emptyString}
            inputProps={{ decimalScale: cvsChAmount, thousandSeparator: true }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <FormControlLabel
            control={
              <Switch id={'rtiInsurance_financedInsurance'} color='primary' disabled checked={(lens.financedInsurance |> get) || false} />
            }
            label={t('Offer.Insurance.FinancedInsurance')}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_financedPeriod'}
            fullWidth
            label={t('Offer.Insurance.FinancedPeriod')}
            value={(lens.financedInsurancePeriod |> get) || emptyString}
            disabled
            isNumeric
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Autocomplete
            id={'rcaInsurance_paymentPeriodicity'}
            fullWidth
            label={t('Offer.Insurance.PaymentPeriodicity')}
            simpleValue
            value={(lens.periodicity |> get) || emptyString}
            onChange={onUpdatePaymentPeriodicity}
            isSearchable={false}
            valueKey='value'
            labelKey='code'
            options={periodicities.map(option => ({
              ...option,
              code: t('Offer.Insurance.' + option.code)
            }))}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            id={'rtiInsurance_insurer'}
            fullWidth
            label={t('Offer.Insurance.Insurer')}
            disabled
            value={(lens.insurerName |> get) || emptyString}
          />
        </Grid>
        {insuranceOfferURL && hasRightToOpenInsurance && (
          <Grid item xs={12} md={6} lg={3}>
            <Paper elevation={0}>
              <Link href={insuranceOfferURL} underline='hover' color='blue' target='_blank' rel='noopener noreferrer'>
                {t('Offer.Insurance.InsuranceOfferNumber', {
                  insuranceOffer: lens.externalInsuranceDocumentNumber |> get
                })}
              </Link>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

RtiInsuranceGeneralData.propTypes = {
  lens: PropTypes.object.isRequired,
  onUpdatePaymentPeriodicity: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
};

export default RtiInsuranceGeneralData;
